// client/src/utils/leagueDeletePermissions.js

/**
 * League deletion permission utilities
 *
 * Centralized logic for determining if a league can be deleted and
 * generating appropriate UI button properties based on permissions.
 */

import { LEAGUE_STARTED_STATUSES } from '../constants/leagueStatuses'

/**
 * Check if a league can be deleted based on league status and user permissions
 * @param {Object} league - League object with league_status property
 * @param {string} userType - User type ('admin', 'owner', etc.)
 * @returns {Object} Object with canDelete boolean and optional reason string
 */
export function canDeleteLeague(league, userType) {
  if (!league || !userType) {
    return { canDelete: false, reason: 'Authentication required' }
  }

  const isLeagueStarted = LEAGUE_STARTED_STATUSES.includes(league.league_status)

  // Before league starts: Both admin and owner can delete
  if (!isLeagueStarted) {
    if (!['admin', 'owner'].includes(userType)) {
      return {
        canDelete: false,
        reason: 'Admin or owner privileges required',
      }
    }
    return { canDelete: true }
  }

  // After league starts: Only owner can delete
  if (userType !== 'owner') {
    return {
      canDelete: false,
      reason: 'Only the league owner can delete leagues that have started',
    }
  }

  // Additional status-specific restrictions
  if (league.league_status === 'active') {
    return {
      canDelete: false,
      reason: 'Cannot delete active league with matches in progress',
    }
  }

  if (league.league_status === 'completed') {
    return { canDelete: false, reason: 'Cannot delete completed league' }
  }

  return { canDelete: true }
}

/**
 * Get button properties for league delete button based on permissions
 * @param {Object} league - League object with league_status property
 * @param {string} userType - User type ('admin', 'owner', etc.)
 * @returns {Object} Object with disabled, title, and className properties for button
 */
export function getDeleteButtonProps(league, userType) {
  const deleteCheck = canDeleteLeague(league, userType)

  if (!deleteCheck.canDelete) {
    return {
      disabled: true,
      title: deleteCheck.reason,
      className: 'btn btn-sm btn-secondary',
    }
  }

  return {
    disabled: false,
    title: 'Delete league - this action cannot be undone',
    className: 'btn btn-sm btn-danger',
  }
}
